import { Component, Inject, OnInit } from '@angular/core';
import { MAT_LEGACY_DIALOG_DATA as MAT_DIALOG_DATA, MatLegacyDialog as MatDialog, MatLegacyDialogRef as MatDialogRef } from '@angular/material/legacy-dialog';
import { CustomEvents, Utils } from '@app/_helpers';
import { ConfigurationService, EventsService } from '@app/_shared/providers';
import { TableColumnsService } from '@app/_shared/providers/table-columns/table-columns.service';
import { ShowPromotePlan } from '@app/_shared/shared.enums';
import { PROMOTION_PLAN_CAPTION } from '@app/_shared/strings';
import { Exchange } from '@app/app.structs';
import { AlgoPermissionMapping } from '@app/modules/admin/components/plan-n-pricing/components/plan-pricing-detail/components/algo-permissions-form/algo-permissions-mapping';
import { PlanDetailI, PricingI } from '@app/modules/admin/providers/plan-n-pricing/plan-n-pricing.interfaces';
import { AuthService, SocialLoginProvider } from '@app/modules/auth';
import { MbPlansService } from '@app/modules/trading/modules/mb-plans/providers/mb-plans.service';
import { LoginSource } from '@app/modules/trading/modules/profile/components/personal-details/interfaces/personal-details.enum';
import { BrokerPlanDetails } from '@app/modules/trading/modules/profile/components/plans/plans.interface';
import { PlanType } from '../../../../utProto/webEnums_pb';
import { BrokerPlanPaymentOptionsComponent } from '../broker-plan-payment-options/broker-plan-payment-options.component';
import { PaymentResultComponent } from '../payment-result/payment-result.component';

@Component({
  selector: 'app-promote-plan',
  templateUrl: './promote-plan.component.html',
  styleUrls: ['./promote-plan.component.scss']
})
export class PromotePlanComponent implements OnInit {
  isShareIndia: boolean;
  AlgoPermissionMapping = AlgoPermissionMapping;
  brokerage_value: number;
  brokerPlanInfo: PlanDetailI;
  planId: number;
  discounted_brokerage_value: number;
  discount: number;
  symbolsMap: Map<Exchange,string> = new Map();
  Exchange = Exchange;
  showNseEllipsis: boolean = true;
  showBseEllipsis: boolean = true;
  promotionPlanCaption: string = PROMOTION_PLAN_CAPTION;
  isRestricted: boolean;
  frequency: string;

  constructor(
    @Inject(MAT_DIALOG_DATA) public brokerPlanDetails,
    private dialogRef: MatDialogRef<PromotePlanComponent>,
    private dialog: MatDialog,
    private authService: AuthService,
    private tableService: TableColumnsService,
    private mbPlansService: MbPlansService,
    private configurationServie: ConfigurationService,
    private eventService: EventsService
  ) {
    this.isShareIndia = this.authService.accessTokenObj.loginSource === LoginSource.SHARE_INDIA;

  }

  ngOnInit(): void {
    this.brokerPlanInfo = this.brokerPlanDetails.planInfo;
    this.symbolsMap = Utils.getSymbolsForPlan(this.brokerPlanInfo);
    if (this.configurationServie.remoteConfigCacheObj?.PROMOTION_PLAN_CAPTION) {
      this.promotionPlanCaption = this.configurationServie.remoteConfigCacheObj.PROMOTION_PLAN_CAPTION;
    }
    this.getBrokerageValue();
    this.paymentResult();
    const frequencyKey = this.brokerPlanDetails.duration.toString() + '_' +  this.brokerPlanDetails.frequency;
    this.frequency = this.configurationServie.remoteConfigCacheObj.FREQUENCY_NAME_MAPPING[frequencyKey];
  }

  getTickByAlgoPermissions(id: number) {
    return this.getTick(this.brokerPlanInfo.algo_permissions.indexOf(id) > -1)
  }

  getTick(check: boolean) {
    return check ? 'tick' : 'cancel';
  }

  /**
   * Connects the broker by retrieving the server configuration, generating an authentication link,
   * and redirecting the user to the broker's signup page.
   */
  connectBroker() {
    this.configurationServie.getServerConfig().subscribe((res) => {
      const url = res.config_params.SIGNUP_URL;
      const code = Utils.searchQueryParam(url, 'referral');
      const userName = this.authService.accessTokenObj?.clientId;
      this.authService.initSocialLogin(SocialLoginProvider.SHARE_INDIA, true).then(pr => {
        pr.subscribe((res) => {
          const authLink = Utils.addParamToStringUrl(res.auth_link, 'referral', `${code}-${userName}`);
          location.replace(authLink);
        });
      });
    });
  }

  subscribePlan() {
    if (this.isShareIndia) {
      const data: BrokerPlanDetails = {
       id: this.planId,
       brokerage_value: this.brokerage_value,
       isPromotePlan: true,
       planName: this.brokerPlanInfo.plan_name,
       isRestrictedPlan: this.isRestricted,
       isAutopayOff: this.isRestricted
      }
      const dialogRef = this.dialog.open(BrokerPlanPaymentOptionsComponent, {
        width: '400px',
        data
      });
    } else {
      this.connectBroker();
    }
  }

  paymentResult() {
    this.eventService.subscribe(CustomEvents.PaymentResult, () => {
      const PaymentResultDialogRef = this.dialog.open(PaymentResultComponent, {
        panelClass: ['padding-dialog', 'dialog-rouded-edges'],
        height: '300px',
        width: '400px',
        disableClose: true,
        data: {
          brokerPlanBrokerageValue: this.brokerage_value
        }
      })

      PaymentResultDialogRef.afterClosed().subscribe(() => {
        this.mbPlansService.getCurrentPlan(true).subscribe();
        this.mbPlansService.getUserDetails(true).subscribe();
        this.closeDialog();
      })
    })

  }

  closeDialog() {
    this.tableService.setColumns({ key: ShowPromotePlan, data: ['true'] }, false).subscribe();
    this.dialogRef?.close()
  }

  getBrokerageValue() {
    this.brokerPlanInfo.pricing.forEach((price: PricingI) => {
      if (price?.frequency === this.brokerPlanDetails?.frequency) {
        this.brokerage_value = price.brokerage_value;
        this.discounted_brokerage_value = price.discounted_brokerage_value;
        this.planId = price.id;
        this.discount = price.discount;
        this.isRestricted = (price.plan_type == PlanType.PLANTYPE_ONETIME);
      }
    })
  }

  /**
   * Toggles the ellipsis for NSE or BSE based on the provided flag.
   * If `isNSE` is true, it toggles the ellipsis for NSE. Otherwise, it toggles the ellipsis for BSE.
   * Additionally, it scrolls the provided element to the leftmost position.
   * 
   * @param isNSE - A boolean flag indicating whether to toggle the ellipsis for NSE.
   * @param el - The HTML element to scroll to the leftmost position.
   */
  toggleEllipsis(isNSE: boolean, el: HTMLElement){
    if(isNSE){
      this.showNseEllipsis = !this.showNseEllipsis;
    } else{
      this.showBseEllipsis = !this.showBseEllipsis;
    }
    // scrollLeft return current position related to the left most position
    // so i am setting it to 0 to make it scroll to the left most position
    el.scrollLeft -= el.scrollLeft;
  }
}
